import * as React from 'react'

import * as date from '@owl-nest/date'
import { useInterval } from '@owl-nest/hooks'
import { t } from '@owl-nest/localize'

type DateCountdown = {
  endingSoon: boolean
  label: string
  value: number | string
}

// Get ready to get blown away by the following time arithmetics
const SECOND = 1
const MINUTE = SECOND * 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24

export function useDateCountdown(
  endDate: Date,
  params = { automaticRefresh: true, shortLabels: false },
): DateCountdown {
  const differenceInSecondsBetweenEndDateAndNow = date.differenceInSeconds(endDate, Date.now())
  const differenceInHoursBetweenEndDateAndNow = Math.round(differenceInSecondsBetweenEndDateAndNow / HOUR)
  const differenceInDaysBetweenEndDateAndNow = Math.round(differenceInSecondsBetweenEndDateAndNow / DAY)

  const [countdown, setCountdown] = React.useState<DateCountdown>(getCountDown(endDate, params.shortLabels))

  const getInitialRefreshInterval = (): number | null => {
    if (
      !(params.automaticRefresh && differenceInHoursBetweenEndDateAndNow >= 0) ||
      differenceInSecondsBetweenEndDateAndNow <= 0
    )
      return null
    if (differenceInHoursBetweenEndDateAndNow <= 1) return 1000
    if (differenceInHoursBetweenEndDateAndNow <= 6) return 1000 * 60
    return 1000 * 60 * 60
  }
  const [refreshInterval, setRefreshInterval] = React.useState(getInitialRefreshInterval())

  useInterval(() => {
    // Reload the page if project ends while user is viewing the page
    if (differenceInSecondsBetweenEndDateAndNow <= 0) {
      window.location.reload()
    }
    // Trigger live reload every second when reaching 1 hour left (and 1 minute, for convenience)
    if (differenceInSecondsBetweenEndDateAndNow <= 60 * 60 + 60) {
      if (refreshInterval !== 1000) setRefreshInterval(1000)
      // If we aren't under 1 hour but just reached less than 6 hours left, set refresh to every minute
    } else if (differenceInSecondsBetweenEndDateAndNow <= 60 * 60 * 6) {
      if (refreshInterval !== 1000 * 60) setRefreshInterval(1000 * 60)
    }

    setCountdown(getCountDown(endDate, params.shortLabels))
  }, refreshInterval)

  return countdown
}

export function getCountDown(endDate: Date, short: boolean): DateCountdown {
  const differenceInSecondsBetweenEndDateAndNow = date.differenceInSeconds(endDate, Date.now())
  const differenceInHoursBetweenEndDateAndNow = Math.round(differenceInSecondsBetweenEndDateAndNow / HOUR)

  // In short mode, live timer is shown when end date is <= 24h, otherwise when <= 6 hours
  const timeInSecond = short ? 60 * 60 : 60 * 60 * 6

  if (differenceInSecondsBetweenEndDateAndNow <= timeInSecond) {
    if (differenceInSecondsBetweenEndDateAndNow <= 0) {
      return {
        endingSoon: true,
        label: short ? t('Finished') : t('campaign ended'),
        value: '-',
      }
    } else {
      return {
        endingSoon: true,
        label: short ? formatSecondsToCountdown(differenceInSecondsBetweenEndDateAndNow, short) : t('before the end'),
        value: formatSecondsToCountdown(differenceInSecondsBetweenEndDateAndNow, false),
      }
    }
  } else if (differenceInHoursBetweenEndDateAndNow <= 48) {
    return {
      endingSoon: true,
      label: short ? t('H-%(nb)s', { nb: differenceInHoursBetweenEndDateAndNow }) : t('hours left'),
      value: differenceInHoursBetweenEndDateAndNow,
    }
  }

  // Remaining time is > 48hours
  const differenceInDaysBetweenEndDateAndNow = Math.round(differenceInHoursBetweenEndDateAndNow / 24)
  return {
    endingSoon: false,
    label: short ? t('D-%(nb)s', { nb: differenceInDaysBetweenEndDateAndNow }) : t('days left'),
    value: differenceInDaysBetweenEndDateAndNow,
  }
}

/**
 * Formats given seconds in countdown format: <HH>h<MM> if there is more than one hour, <MM>:<SS> otherwise.
 *
 * @param secondsAmount
 */
export function formatSecondsToCountdown(secondsAmount: number, short?: boolean): string {
  const normalizeTime = (time: string): string => (time.length === 1 ? `0${time}` : time)

  const milliseconds = secondsAmount * 1000
  const futureDate = new Date(milliseconds)
  const timezoneDiff = futureDate.getTimezoneOffset() / 60
  const dateWithoutTimezoneDiff = date.addHours(futureDate, timezoneDiff)

  const hours = normalizeTime(String(date.getHours(dateWithoutTimezoneDiff)))
  const minutes = normalizeTime(String(date.getMinutes(dateWithoutTimezoneDiff)))
  const seconds = normalizeTime(String(date.getSeconds(dateWithoutTimezoneDiff)))

  const hoursOutput = hours !== '00' ? `${hours}h` : ''
  const secondsOutput = hours === '00' ? (short ? `${seconds}` : `:${seconds}`) : ''

  // NOTE: Different translations are needed because they depend on usage context.
  // In short mode (e.g. Project cards), labels only show minutes and seconds, whereas full mode also features hours.
  if (short) {
    return t('%(min)s min %(sec)s sec', {
      min: minutes,
      sec: secondsOutput,
    })
  } else {
    return `${hoursOutput}${minutes}${secondsOutput}`
  }
}
